<script lang="ts">
	import { onMount } from 'svelte';
	import { _ } from 'svelte-i18n';
	
	// State
	let searchQuery = $state('');
	let isOpen = $state(false);
	let searchInput = $state<HTMLInputElement | null>(null);
	
	// Props for parent component
	interface Props {
		onSearch?: (query: string) => void;
		placeholder?: string;
	}
	
	let { onSearch, placeholder = 'Search updates...' }: Props = $props();
	
	// Handle search input changes
	$effect(() => {
		if (onSearch) {
			onSearch(searchQuery);
		}
	});
	
	// Global keyboard shortcuts
	onMount(() => {
		function handleGlobalKeydown(e: KeyboardEvent) {
			// Cmd/Ctrl + K to open search
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault();
				openSearch();
			}
			
			// Forward slash to open search (when not in input)
			if (e.key === '/' && !isInputElement(e.target)) {
				e.preventDefault();
				openSearch();
			}
			
			// Escape to close search
			if (e.key === 'Escape' && isOpen) {
				e.preventDefault();
				closeSearch();
			}
		}
		
		document.addEventListener('keydown', handleGlobalKeydown);
		
		return () => {
			document.removeEventListener('keydown', handleGlobalKeydown);
		};
	});
	
	function isInputElement(target: any): boolean {
		return target && (
			target.tagName === 'INPUT' ||
			target.tagName === 'TEXTAREA' ||
			target.contentEditable === 'true'
		);
	}
	
	function openSearch() {
		isOpen = true;
		setTimeout(() => searchInput?.focus(), 50);
	}
	
	function closeSearch() {
		isOpen = false;
	}
</script>

<!-- Search Button -->
<button 
	class="search-button"
	onclick={openSearch}
	aria-label={$_('weekly.search.ariaLabel')}
>
	<svg class="search-button__icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
		<path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
	</svg>
	<span class="search-button__text">{placeholder}</span>
	<kbd class="search-button__shortcut">⌘K</kbd>
</button>

<!-- Search Modal (simplified) -->
{#if isOpen}
	<div class="search-modal" role="dialog" aria-modal="true">
		<!-- Backdrop -->
		<button 
			class="search-modal__backdrop" 
			onclick={closeSearch}
			aria-label={$_('components.weeklySearch.closeSearch')}
			tabindex="-1"
		></button>
		
		<!-- Dialog -->
		<div class="search-modal__dialog">
			<!-- Search Input -->
			<div class="search-modal__header">
				<div class="search-modal__input-wrapper">
					<svg class="search-modal__input-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
						<path d="M14.386 14.386l4.0877 4.0877-4.0877-4.0877c-2.9418 2.9419-7.7115 2.9419-10.6533 0-2.9419-2.9418-2.9419-7.7115 0-10.6533 2.9418-2.9419 7.7115-2.9419 10.6533 0 2.9419 2.9418 2.9419 7.7115 0 10.6533z" stroke="currentColor" fill="none" stroke-width="2"/>
					</svg>
					<input
						bind:this={searchInput}
						bind:value={searchQuery}
						type="text"
						class="search-modal__input"
						placeholder={placeholder}
						autocomplete="off"
						spellcheck="false"
					/>
					{#if searchQuery}
						<button 
							class="search-modal__clear"
							onclick={() => searchQuery = ''}
							aria-label={$_('components.weeklySearch.clearSearch')}
						>
							<svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
								<path d="M7 0C3.134 0 0 3.134 0 7s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7zm2.828 9.172a.5.5 0 01-.707.707L7 7.707 4.879 9.828a.5.5 0 01-.707-.707L6.293 7 4.172 4.879a.5.5 0 01.707-.707L7 6.293l2.121-2.121a.5.5 0 01.707.707L7.707 7l2.121 2.172z"/>
							</svg>
						</button>
					{/if}
					<button 
						class="search-modal__close"
						onclick={closeSearch}
						aria-label={$_('components.weeklySearch.closeSearch')}
					>
						<kbd>ESC</kbd>
					</button>
				</div>
			</div>
			
			<!-- Simple hint -->
			<div class="search-modal__hint">
				<p>{searchQuery ? $_('weekly.search.searching', { values: { query: searchQuery }}) : $_('weekly.search.hint')}</p>
			</div>
		</div>
	</div>
{/if}

<style>
	/* Search Button */
	.search-button {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 1rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 8px;
		color: var(--text-secondary);
		cursor: pointer;
		transition: all 0.2s ease;
		font-size: 0.875rem;
		min-width: 200px;
	}
	
	.search-button:hover {
		border-color: var(--border-default);
		background: var(--hover-overlay);
		color: var(--text-primary);
	}
	
	.search-button__icon {
		width: 16px;
		height: 16px;
		flex-shrink: 0;
	}
	
	.search-button__text {
		flex: 1;
		text-align: left;
	}
	
	.search-button__shortcut {
		margin-left: auto;
		padding: 0.125rem 0.375rem;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: 4px;
		font-size: 0.75rem;
		font-family: monospace;
		color: var(--text-tertiary);
	}
	
	/* Search Modal */
	.search-modal {
		position: fixed;
		inset: 0;
		z-index: 9999;
		display: flex;
		align-items: flex-start;
		justify-content: center;
		padding: 10vh 1rem;
	}
	
	.search-modal__backdrop {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.5);
		backdrop-filter: blur(4px);
		border: none;
		cursor: pointer;
		padding: 0;
	}
	
	.search-modal__dialog {
		position: relative;
		width: 100%;
		max-width: 600px;
		background: var(--bg-primary);
		border-radius: 12px;
		box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
		overflow: hidden;
		animation: modalSlideIn 0.2s ease;
	}
	
	@keyframes modalSlideIn {
		from {
			opacity: 0;
			transform: translateY(-20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
	
	/* Search Input */
	.search-modal__header {
		border-bottom: 1px solid var(--border-light);
	}
	
	.search-modal__input-wrapper {
		display: flex;
		align-items: center;
		padding: 1rem;
		gap: 0.75rem;
	}
	
	.search-modal__input-icon {
		color: var(--text-tertiary);
		flex-shrink: 0;
	}
	
	.search-modal__input {
		flex: 1;
		background: none;
		border: none;
		font-size: 1.125rem;
		color: var(--text-primary);
		outline: none;
	}
	
	.search-modal__input::placeholder {
		color: var(--text-tertiary);
	}
	
	.search-modal__clear,
	.search-modal__close {
		padding: 0.25rem;
		background: none;
		border: none;
		color: var(--text-tertiary);
		cursor: pointer;
		border-radius: 4px;
		transition: all 0.2s ease;
	}
	
	.search-modal__clear:hover,
	.search-modal__close:hover {
		background: var(--bg-tertiary);
		color: var(--text-primary);
	}
	
	.search-modal__close kbd {
		padding: 0.25rem 0.5rem;
		background: var(--bg-tertiary);
		border: 1px solid var(--border-light);
		border-radius: 4px;
		font-size: 0.75rem;
		font-family: monospace;
	}
	
	/* Hint */
	.search-modal__hint {
		padding: 2rem;
		text-align: center;
		color: var(--text-tertiary);
		font-size: 0.875rem;
	}
	
	.search-modal__hint p {
		margin: 0;
	}
	
	/* Mobile */
	@media (max-width: 640px) {
		.search-button__text {
			display: none;
		}
		
		.search-button {
			min-width: auto;
		}
		
		.search-button__shortcut {
			display: none;
		}
	}
</style>